import { ReactNode, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

type MailtoLinkProps = {
  email: string;
  subject?: string;
  body?: string;
  className?: string;
  children?: ReactNode;
};

const MailtoLink = ({ email, subject, body, className, children }: MailtoLinkProps) => {
  const [open, setOpen] = useState(false);

  const mailtoHref = useMemo(() => {
    const params = new URLSearchParams();
    if (subject) params.set("subject", subject);
    if (body) params.set("body", body);
    const query = params.toString();
    return `mailto:${email}${query ? `?${query}` : ""}`;
  }, [email, subject, body]);

  const encodedEmail = encodeURIComponent(email);
  const encodedSubject = encodeURIComponent(subject || "");
  const encodedBody = encodeURIComponent(body || "");

  const openExternalCompose = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(email);
    } finally {
      setOpen(false);
    }
  };

  return (
    <>
      <a
        href={mailtoHref}
        className={className}
        onClick={(e) => {
          e.preventDefault();
          setOpen(true);
        }}
      >
        {children ?? email}
      </a>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Alege aplicația de email</DialogTitle>
            <DialogDescription>
              Selectează cum vrei să deschizi mesajul către {email}.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-2">
            <a
              href={mailtoHref}
              onClick={() => setOpen(false)}
              className="w-full rounded-md border border-border bg-card px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors"
            >
              Aplicația implicită
            </a>
            <button
              type="button"
              onClick={() =>
                openExternalCompose(`https://mail.google.com/mail/?view=cm&fs=1&to=${encodedEmail}&su=${encodedSubject}&body=${encodedBody}`)
              }
              className="w-full rounded-md border border-border bg-card px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors"
            >
              Gmail (web)
            </button>
            <button
              type="button"
              onClick={() =>
                openExternalCompose(`https://outlook.office.com/mail/deeplink/compose?to=${encodedEmail}&subject=${encodedSubject}&body=${encodedBody}`)
              }
              className="w-full rounded-md border border-border bg-card px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors"
            >
              Outlook (web)
            </button>
            <button
              type="button"
              onClick={() =>
                openExternalCompose(`https://compose.mail.yahoo.com/?to=${email}&subject=${encodedSubject}&body=${encodedBody}`)
              }
              className="w-full rounded-md border border-border bg-card px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors"
            >
              Yahoo Mail (web)
            </button>
            <button
              type="button"
              onClick={copyEmail}
              className="w-full rounded-md border border-border bg-card px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors"
            >
              Copiază adresa de email
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MailtoLink;
